import type {
  Point2,
  ViewportSize,
  ViewportState,
} from '@frontend/viewer-core';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Length of a dimension line's end ticks, in px. */
const TICK_PX = 5;

export const create = <K extends keyof SVGElementTagNameMap>(
  tag: K,
): SVGElementTagNameMap[K] =>
  document.createElementNS(SVG_NS, tag) as SVGElementTagNameMap[K];

export const setAttributes = (
  element: Element,
  attributes: Record<string, string | number>,
): void => {
  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, String(value));
  });
};

/**
 * Shows or hides an element and everything under it.
 *
 * `display`, not `visibility`. SVG lets a descendant override a hidden ancestor
 * with `visibility="visible"`, so hiding a group whose children had been shown
 * individually — a dimension line's end ticks, say — left those children on
 * screen after the thing they belonged to was gone. `display: none` prunes the
 * whole subtree and cannot be overridden from inside it.
 */
export const show = (element: Element, isVisible: boolean): void => {
  if (isVisible) {
    element.removeAttribute('display');
  } else {
    element.setAttribute('display', 'none');
  }
};

/** A line with a text label centred on it, reused frame to frame. */
export type LabelledLine = {
  group: SVGGElement;
  line: SVGLineElement;
  startTick: SVGLineElement;
  endTick: SVGLineElement;
  label: SVGTextElement;
};

export const createLabelledLine = (
  color: string,
  isDashed = false,
): LabelledLine => {
  const group = create('g');
  const line = create('line');
  setAttributes(line, { stroke: color, 'stroke-width': 1.25 });
  if (isDashed) {
    line.setAttribute('stroke-dasharray', '6 4');
  }
  const startTick = create('line');
  const endTick = create('line');
  [startTick, endTick].forEach((tick) => {
    setAttributes(tick, { stroke: color, 'stroke-width': 1.25 });
    group.appendChild(tick);
  });
  const label = create('text');
  setAttributes(label, {
    fill: color,
    'font-size': 11,
    'text-anchor': 'middle',
    'dominant-baseline': 'central',
    'paint-order': 'stroke',
    stroke: 'rgba(30, 30, 30, 0.85)',
    'stroke-width': 3,
    'stroke-linejoin': 'round',
  });
  group.appendChild(line);
  group.appendChild(label);
  return { group, line, startTick, endTick, label };
};

export const placeLabelledLine = (
  parts: LabelledLine,
  from: Point2,
  to: Point2,
  text: string,
  { withTicks = true } = {},
): void => {
  setAttributes(parts.line, {
    x1: from[0],
    y1: from[1],
    x2: to[0],
    y2: to[1],
  });

  // Ticks stand perpendicular to the line, which is what makes a dimension read
  // as a measurement rather than as more geometry.
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const length = Math.hypot(dx, dy) || 1;
  const normalX = (-dy / length) * TICK_PX;
  const normalY = (dx / length) * TICK_PX;

  show(parts.startTick, withTicks);
  show(parts.endTick, withTicks);
  if (withTicks) {
    setAttributes(parts.startTick, {
      x1: from[0] - normalX,
      y1: from[1] - normalY,
      x2: from[0] + normalX,
      y2: from[1] + normalY,
    });
    setAttributes(parts.endTick, {
      x1: to[0] - normalX,
      y1: to[1] - normalY,
      x2: to[0] + normalX,
      y2: to[1] + normalY,
    });
  }

  setAttributes(parts.label, {
    x: (from[0] + to[0]) / 2 + normalX * 2,
    y: (from[1] + to[1]) / 2 + normalY * 2,
  });
  parts.label.textContent = text;
};

export type ToolFrameSubscription = (onChange: () => void) => () => void;

/**
 * Viewport surface a tool/text overlay needs: project world→screen, read the
 * live view state, and redraw when the view or container size moves.
 */
export type ToolViewport = {
  getState: () => ViewportState;
  worldToScreen: (world: Point2) => Point2;
  subscribe: ToolFrameSubscription;
  size: {
    getState: () => ViewportSize;
    subscribe: ToolFrameSubscription;
  };
};

export type OverlayFrameOptions = {
  /** Runs once immediately, then on each coalesced store notification. */
  draw: () => void;
  subscriptions: readonly ToolFrameSubscription[];
  /** Extra teardown after the frame and subscriptions are cancelled. */
  onCleanup?: () => void;
};

/**
 * Coalesces store notifications onto one animation frame. Same budget as the
 * WebGL layer: a burst of pointer events produces one draw rather than one per
 * event. Host-specific teardown (clearing an SVG, etc.) goes in `onCleanup`.
 */
export const bindOverlayFrame = ({
  draw,
  subscriptions,
  onCleanup,
}: OverlayFrameOptions): (() => void) => {
  let pendingFrame: number | null = null;

  const run = () => {
    pendingFrame = null;
    draw();
  };

  const schedule = () => {
    if (pendingFrame === null) {
      pendingFrame = requestAnimationFrame(run);
    }
  };

  const unsubscribes = subscriptions.map((subscribe) => subscribe(schedule));
  run();

  return () => {
    if (pendingFrame !== null) {
      cancelAnimationFrame(pendingFrame);
    }
    unsubscribes.forEach((unsubscribe) => unsubscribe());
    onCleanup?.();
  };
};
